"use client";

import React, { useMemo } from "react";
import {
  formatDividendsDate,
  formatDividendsNumberData,
} from "@components/StockDetails/StockDividends/DividendsUtils";

import { ColumnDef } from "@tanstack/react-table";
import type { FormattedDividend } from "@components/StockDetails/StockDividends/types";
import { StockDetailTable } from "@components/StockDetails/StockDetailTable";
import { useStockDetailsMarket } from "@hooks/useStockDetailsMarket";
import { ViewDetailsLink, buildStockDetailModuleHref } from "./ViewDetailsLink";

interface AssetDividendHistoryBusinessChartProps {
  assetType: "stock" | "crypto";
  assetRouterId: string;
}

const columns: ColumnDef<FormattedDividend>[] = [
  {
    accessorKey: "ex_dividend_date",
    header: "Ex-Dividend Date",
    meta: {
      width: 213,
      pinned: "left" as const,
      cellClassName: "bg-background_solid_secondary",
    },
  },
  {
    accessorKey: "amount",
    header: "Cash Amount",
    meta: { width: 165, cellClassName: "text-left" },
  },
  {
    accessorKey: "record_date",
    header: "Record Date",
    meta: { width: 165, cellClassName: "text-left" },
  },
  {
    accessorKey: "pay_date",
    header: "Pay Date",
    meta: { width: 165, cellClassName: "text-left" },
  },
];

export const AssetDividendHistoryBusinessChart: React.FC<
  AssetDividendHistoryBusinessChartProps
> = ({ assetRouterId }) => {
  const { data: stockData, isLoading } = useStockDetailsMarket({
    queryParams: {
      ids: [assetRouterId],
      "enrich.enable": true,
      "enrich.with_dividends.enable": true,
      "enrich.with_dividends.limit": 6,
    },
    queryKey: ["chat-dividend-history", assetRouterId],
  });

  const stockSymbol = stockData?.symbol || "";

  const historyData = useMemo(() => {
    const items = stockData?.dividends?.items;
    if (!items?.length) {
      return [];
    }
    return items.slice(0, 6).map((item) => ({
      ...item,
      ex_dividend_date: formatDividendsDate(item.ex_dividend_date || ""),
      record_date: formatDividendsDate(item.record_date || ""),
      pay_date: formatDividendsDate(item.pay_date || ""),
      amount: `$${formatDividendsNumberData(item.adjusted_amount || 0)}`,
    }));
  }, [stockData]);

  if (isLoading) {
    return (
      <div className="h-40 w-full animate-pulse bg-background_white_overlay5" />
    );
  }

  if (!historyData.length) {
    return null;
  }

  return (
    <>
      <div className="w-full overflow-hidden">
        <StockDetailTable
          data={historyData}
          columns={columns}
          getRowId={(row) => row.ex_dividend_date || ""}
        />
      </div>
      {stockSymbol && (
        <ViewDetailsLink
          href={buildStockDetailModuleHref(stockSymbol, "dividends")}
        />
      )}
    </>
  );
};
